import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { mediaList } from "@/mocks/media";
import { newsList } from "@/mocks/news";
import type { MediaItem, NewsItem } from "@/types/media";
import { useSeo } from "@/hooks/useSeo";
import MediaCard from "@/components/feature/MediaCard";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/base/EmptyState";

type Tab = "all" | "media" | "news";

const TABS: { value: Tab; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "media", label: "影视作品" },
  { value: "news", label: "资讯" },
];

function matchesMedia(item: MediaItem, query: string) {
  const haystack = [
    item.title,
    item.type,
    item.region,
    item.director,
    item.summary,
    item.genres.join(" "),
    item.cast.join(" "),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

function matchesNews(item: NewsItem, query: string) {
  const haystack = [
    item.title,
    item.summary,
    item.category,
    item.source,
    item.content.join(" "),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") || "").trim();
  const [keyword, setKeyword] = useState(query);
  const [tab, setTab] = useState<Tab>("all");

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useSeo({
    title: query ? `“${query}”的搜索结果 - 星空传媒` : "搜索 - 星空传媒",
    description: query
      ? `星空传媒为您展示与“${query}”相关的影视作品与资讯搜索结果。`
      : "在星空传媒搜索影视作品与资讯，快速找到想看的内容。",
    keywords: `星空传媒,影视搜索,${query || "影视检索"}`,
  });

  const lowerQuery = query.toLowerCase();

  const mediaResults = useMemo(
    () =>
      lowerQuery
        ? (mediaList as MediaItem[]).filter((item) => matchesMedia(item, lowerQuery))
        : [],
    [lowerQuery],
  );

  const newsResults = useMemo(
    () =>
      lowerQuery
        ? (newsList as NewsItem[]).filter((item) => matchesNews(item, lowerQuery))
        : [],
    [lowerQuery],
  );

  const recommendations = useMemo(
    () => [...(mediaList as MediaItem[])].sort((a, b) => b.views - a.views).slice(0, 4),
    [],
  );

  const total = mediaResults.length + newsResults.length;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = keyword.trim();
    if (!next) return;
    setSearchParams({ q: next });
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-8 md:py-10">
      <header>
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          内容检索
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          输入关键词，快速查找影视作品与影视资讯（当前为前端演示检索）。
        </p>
        <form
          onSubmit={handleSubmit}
          className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <div className="flex flex-1 items-center gap-2 rounded-md border border-secondary-200 bg-background-50 px-4 py-3 transition-colors focus-within:border-accent-300">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视作品名称、演员、类型或资讯关键词"
              aria-label="搜索关键词"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {keyword && (
              <button
                type="button"
                aria-label="清空关键词"
                onClick={() => setKeyword("")}
                className="flex h-5 w-5 items-center justify-center text-foreground-400 transition-colors hover:text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-circle-line"></i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-accent-600 cursor-pointer"
          >
            <i className="ri-search-line"></i>
            搜索
          </button>
        </form>
      </header>

      {query && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-b border-secondary-200 pb-4">
          <p className="text-sm text-foreground-600">
            关键词：<strong className="text-accent-600">“{query}”</strong>
            <span className="ml-2 text-foreground-400">共 {total} 条结果</span>
          </p>
          <div className="flex items-center gap-1 rounded-full bg-secondary-100 p-1">
            {TABS.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setTab(item.value)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                  tab === item.value
                    ? "bg-background-50 text-accent-600 shadow-sm"
                    : "text-foreground-600 hover:text-accent-600"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {!query ? (
        <div className="mt-8">
          <EmptyState
            icon="ri-search-eye-line"
            title="开始你的影视探索"
            description="在上方输入关键词，即可检索影视作品与影视资讯。试试搜索“星际”“悬疑”或“音乐”。"
          />
          <section>
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-4 w-1 rounded-full bg-accent-500" />
              热门推荐
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {recommendations.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          </section>
        </div>
      ) : total === 0 ? (
        <div className="mt-4">
          <EmptyState
            icon="ri-emotion-sad-line"
            title={`没有找到与“${query}”相关的内容`}
            description="请尝试更换关键词，或浏览下方为您推荐的热门影视作品。"
          />
          <section>
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-4 w-1 rounded-full bg-accent-500" />
              为你推荐
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {recommendations.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          </section>
        </div>
      ) : (
        <div className="mt-8 space-y-10">
          {(tab === "all" || tab === "media") && mediaResults.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                影视作品
                <span className="text-sm font-normal text-foreground-400">
                  ({mediaResults.length})
                </span>
              </h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                {mediaResults.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          )}

          {(tab === "all" || tab === "news") && newsResults.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                影视资讯
                <span className="text-sm font-normal text-foreground-400">
                  ({newsResults.length})
                </span>
              </h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {newsResults.map((item) => (
                  <NewsCard key={item.id} item={item} layout="vertical" />
                ))}
              </div>
            </section>
          )}

          {tab === "media" && mediaResults.length === 0 && (
            <EmptyState
              icon="ri-film-line"
              title="没有匹配的影视作品"
              description="切换到「资讯」标签，或更换关键词后再试试。"
            />
          )}
          {tab === "news" && newsResults.length === 0 && (
            <EmptyState
              icon="ri-newspaper-line"
              title="没有匹配的资讯"
              description="切换到「影视作品」标签，或更换关键词后再试试。"
            />
          )}
        </div>
      )}
    </div>
  );
}